import { useState } from "react";
import { ChampDate } from "@/components/fiche/ChampDate";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const OPERATIONS = ["Pompe à chaleur", "Clim mobile", "Éclairage LED", "Déstratificateur", "Isolation"];

const HEURES = Array.from({ length: 25 }, (_, i) => { const v = `${String(8 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`; return { code: v, label: v.replace(":", " h ") }; });

export const formatDuree = (s) => `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;

const a = (base, jours, h, m) => { const d = new Date(base); d.setDate(d.getDate() + jours); d.setHours(h, m, 0, 0); return d; };

const FERIES = ["1-1", "5-1", "5-8", "7-14", "8-15", "11-1", "11-11", "12-25"];
const slug = (t) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

const raccourcis = () => {
  const now = new Date();
  const ouvre = (d) => { while ([0, 6].includes(d.getDay()) || FERIES.includes(`${d.getMonth() + 1}-${d.getDate()}`)) d.setDate(d.getDate() + 1); return d; };
  const mois = Array.from({ length: 11 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() + i + 2, 1, 10, 0); return { code: `mois-${d.getMonth() + 1}`, label: `En ${MOIS[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ""}`, date: ouvre(d) }; });
  return [
    { code: "1h", label: "Dans 1 h", date: new Date(now.getTime() + 3600e3) },
    ...(now.getHours() < 17 ? [{ code: "soir", label: "Ce soir 18 h", date: a(now, 0, 18, 0) }] : []),
    { code: "dm", label: "Demain matin", date: ouvre(a(now, 1, 9, 30)) },
    { code: "da", label: "Demain après-midi", date: ouvre(a(now, 1, 14, 0)) },
    { code: "s1", label: "Dans 1 semaine", date: a(now, 7, 10, 0) },
    { code: "mois-prochain", label: "Le mois prochain", date: ouvre(new Date(now.getFullYear(), now.getMonth() + 1, now.getDate(), 10, 0)) },
    ...mois,
  ];
};

const Puce = ({ s, conseille, onClick }) => (
  <button type="button" onClick={onClick} className="min-h-[44px] px-2 at-pill text-xs font-bold leading-tight text-center"
    style={{ background: `color-mix(in srgb, ${s.couleur} ${conseille ? 26 : 14}%, transparent)`, color: `color-mix(in srgb, ${s.couleur} 62%, black)`, outline: conseille ? `2px solid ${s.couleur}` : "none" }}
    data-testid={`result-status-${s.code}`}>{s.nom}</button>
);

export function ResultatAppel({ statuts, decroche, onValider, enCours }) {
  const [choix, setChoix] = useState(null);
  const [commentaire, setCommentaire] = useState("");
  const [libre, setLibre] = useState("");
  const [heureLibre, setHeureLibre] = useState("10:00");
  const envoyer = (statut, extra = {}) => onValider({ statut: statut.code, commentaire, ...extra });
  const cliquer = (s) => (["rappel", "opportunite"].includes(s.type) ? setChoix(s) : envoyer(s));
  return (
    <section className="at-card p-4 space-y-3 at-rise" data-testid="call-result-panel">
      <p className="font-bold">{choix ? (choix.type === "rappel" ? "Quand rappeler ?" : "Quelle opération ?") : "Résultat de l'appel"}</p>
      {!choix && (
        <div className="grid grid-cols-2 gap-2" data-testid="result-status-grid">
          {statuts.map((s) => <Puce key={s.code} s={s} conseille={decroche === false && s.type === "sans_reponse"} onClick={() => !enCours && cliquer(s)} />)}
        </div>
      )}
      {choix?.type === "rappel" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2" data-testid="callback-shortcuts">
            {raccourcis().map((r) => (
              <button key={r.code} type="button" disabled={enCours} onClick={() => envoyer(choix, { rappel_le: r.date.toISOString() })} className="at-btn at-btn-ghost !px-2 text-sm" data-testid={`callback-${r.code}`}>{r.label}</button>
            ))}
          </div>
          <div className="grid grid-cols-[1fr_96px_auto] gap-2">
            <ChampDate valeur={libre} onChange={setLibre} testId="callback-custom-date" />
            <ChoixListe valeur={heureLibre} onChange={setHeureLibre} options={HEURES} testId="callback-custom-time" />
            <button type="button" disabled={!libre || enCours} onClick={() => envoyer(choix, { rappel_le: new Date(`${libre}T${heureLibre}:00`).toISOString() })} className="at-btn at-btn-primary shrink-0" data-testid="callback-custom-save">OK</button>
          </div>
        </div>
      )}
      {choix?.type === "opportunite" && (
        <div className="grid grid-cols-2 gap-2" data-testid="opportunity-operations">
          {OPERATIONS.map((o) => <button key={o} type="button" disabled={enCours} onClick={() => envoyer(choix, { operation: o })} className="at-btn at-btn-ghost !px-2 text-sm" data-testid={`opportunity-op-${slug(o)}`}>{o}</button>)}
        </div>
      )}
      <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={2} maxLength={500} placeholder="Commentaire (facultatif)" className="at-input !h-auto py-2" data-testid="call-comment-input" />
      {choix && <button type="button" onClick={() => setChoix(null)} className="at-btn at-btn-ghost w-full" data-testid="result-back">Changer de statut</button>}
      <p className="text-xs at-muted">Aucun SMS n'est envoyé au contact.</p>
    </section>
  );
}
